import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, Download, Info, Loader2, LockKeyhole, Pencil, Plus, RefreshCcw, ScrollText, Search, ShieldCheck, Trash2, Upload, X } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { EmptyState, SearchEmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import { getStringTable, getStringTableEditAccess, type StringTableEditAccess, patchStringTable } from '../lib/api/stringTables'

type StringEntry = {
  id: string
  english: string
  french: string
  section: string
}

type InlineEdit = {
  entryId: string
  english: string
  french: string
  /** When adding a new entry, this holds the new ID being typed */
  newId?: string
  isNew?: boolean
}

const normalizeId = (value: string) => value.trim().toUpperCase()

const deriveSection = (id: string) => {
  const normalized = normalizeId(id)
  if (!normalized) {
    return 'Uncategorized'
  }
  if (!normalized.startsWith('ID_')) {
    return 'Custom'
  }
  const parts = normalized.split('_').filter(Boolean)
  return parts.length >= 2 ? parts[1] : 'General'
}

const sortEntries = (entries: StringEntry[]) => {
  return [...entries].sort((a, b) => {
    if (a.section !== b.section) {
      return a.section.localeCompare(b.section)
    }
    return a.id.localeCompare(b.id)
  })
}

/** Derive prefix for a new entry from an existing section name, e.g. "HOME" → "ID_HOME_" */
const sectionToPrefix = (section: string) => {
  if (section === 'Uncategorized' || section === 'Custom') {
    return 'ID_'
  }
  return `ID_${section}_`
}

/** Extract placeholder names from a string value, e.g. "Hello {name}" → ["name"] */
const extractPlaceholders = (value: string): string[] => {
  const names: string[] = []
  const re = /\{(\w+)\}/g
  let match: RegExpExecArray | null
  while ((match = re.exec(value)) !== null) {
    names.push(match[1])
  }
  return names
}

/** Compare EN/FR placeholders and return missing/extra names */
const getPlaceholderMismatch = (english: string, french: string) => {
  const enNames = new Set(extractPlaceholders(english))
  const frNames = new Set(extractPlaceholders(french))
  if (enNames.size === 0 && frNames.size === 0) return null

  const missingInFr = [...enNames].filter((n) => !frNames.has(n))
  const extraInFr = [...frNames].filter((n) => !enNames.has(n))
  if (missingInFr.length === 0 && extraInFr.length === 0) return null
  return { missingInFr, extraInFr }
}

/** Renders text with {placeholder} tokens highlighted as badges */
function HighlightedValue({ text }: { text: string }) {
  if (!text) return <span>—</span>
  const parts = text.split(/(\{\w+\})/g)
  return (
    <span>
      {parts.map((part, i) =>
        /^\{\w+\}$/.test(part) ? (
          <code
            key={i}
            className="mx-0.5 inline-block rounded bg-violet-100 px-1.5 py-0.5 text-xs font-semibold text-violet-700 ring-1 ring-violet-200"
          >
            {part}
          </code>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  )
}

/** Warning banner when EN/FR placeholders don't match */
function PlaceholderMismatchWarning({ english, french }: { english: string; french: string }) {
  const mismatch = getPlaceholderMismatch(english, french)
  if (!mismatch) return null

  const parts: string[] = []
  if (mismatch.missingInFr.length > 0) {
    parts.push(`French is missing: ${mismatch.missingInFr.map((n) => `{${n}}`).join(', ')}`)
  }
  if (mismatch.extraInFr.length > 0) {
    parts.push(`French has extra: ${mismatch.extraInFr.map((n) => `{${n}}`).join(', ')}`)
  }

  return (
    <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>Placeholder mismatch — {parts.join('. ')}. This may be a translation error.</span>
    </div>
  )
}

export const StringTablesPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const [entries, setEntries] = useState<StringEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [inlineEdit, setInlineEdit] = useState<InlineEdit | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [sectionFilter, setSectionFilter] = useState('All')
  const [versions, setVersions] = useState({ en: 0, fr: 0 })
  const [skipExistingOnImport, setSkipExistingOnImport] = useState(true)
  const [editAccess, setEditAccess] = useState<StringTableEditAccess | null>(null)

  const role = (user?.role ?? 'Guest').toString()
  const isAdmin = role === 'Admin'
  const canEdit = editAccess?.canEdit ?? false

  const loadTables = useCallback(async () => {
    setLoading(true)
    try {
      const [englishTable, frenchTable, access] = await Promise.all([
        getStringTable('en'),
        getStringTable('fr'),
        getStringTableEditAccess()
      ])

      const englishStrings = englishTable?.strings ?? {}
      const frenchStrings = frenchTable?.strings ?? {}
      const allKeys = new Set([...Object.keys(englishStrings), ...Object.keys(frenchStrings)])

      const nextEntries = Array.from(allKeys).map((key) => ({
        id: key,
        english: englishStrings[key] ?? '',
        french: frenchStrings[key] ?? '',
        section: deriveSection(key)
      }))

      setEntries(sortEntries(nextEntries))
      setVersions({ en: englishTable?.version ?? 0, fr: frenchTable?.version ?? 0 })
      setEditAccess(access)
      if (!access.canEdit) {
        setInlineEdit(null)
      }
    } catch (error) {
      console.error('Failed to load string tables', error)
      addToast({
        title: 'Could not load string tables',
        description: 'Please refresh or try again later.'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false)
      return
    }
    void loadTables()
  }, [isAdmin, loadTables])

  const sections = useMemo(() => {
    const unique = new Set(entries.map((entry) => entry.section))
    return ['All', ...Array.from(unique).sort((a, b) => a.localeCompare(b))]
  }, [entries])

  const filteredEntries = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    return entries.filter((entry) => {
      const matchesSection = sectionFilter === 'All' || entry.section === sectionFilter
      if (!matchesSection) {
        return false
      }
      if (!normalizedSearch) {
        return true
      }
      return (
        entry.id.toLowerCase().includes(normalizedSearch) ||
        entry.english.toLowerCase().includes(normalizedSearch) ||
        entry.french.toLowerCase().includes(normalizedSearch)
      )
    })
  }, [entries, searchTerm, sectionFilter])

  const groupedEntries = useMemo(() => {
    const grouped: Record<string, StringEntry[]> = {}
    for (const entry of filteredEntries) {
      if (!grouped[entry.section]) {
        grouped[entry.section] = []
      }
      grouped[entry.section].push(entry)
    }
    return Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .map((section) => ({
        section,
        entries: grouped[section].sort((a, b) => a.id.localeCompare(b.id))
      }))
  }, [filteredEntries])

  const buildPayloads = (nextEntries: StringEntry[]) => {
    const english: Record<string, string> = {}
    const french: Record<string, string> = {}
    const previous = new Map(entries.map(entry => [entry.id, entry]))
    for (const entry of nextEntries) {
      const before = previous.get(entry.id)
      if (!before || entry.english !== before.english) english[entry.id] = entry.english
      if (!before || entry.french !== before.french) french[entry.id] = entry.french
    }
    const nextIds = new Set(nextEntries.map(entry => entry.id))
    const deletedIds = entries.filter(entry => !nextIds.has(entry.id)).map(entry => entry.id)
    return { english, french, deletedIds }
  }

  const persistEntries = async (nextEntries: StringEntry[]) => {
    if (!canEdit) {
      addToast({
        title: 'String tables are read only',
        description: editAccess?.message ?? 'Manual editing is not allowed in this environment.'
      })
      return
    }

    setSaving(true)
    try {
      const payloads = buildPayloads(nextEntries)
      const save = async (language: string, strings: Record<string, string>) =>
        Object.keys(strings).length || payloads.deletedIds.length
          ? patchStringTable(language, { strings, deletedIds: payloads.deletedIds })
          : getStringTable(language)
      const results = await Promise.allSettled([
        save('en', payloads.english), save('fr', payloads.french)
      ])
      const failed = results.flatMap((result, i) => result.status === 'rejected' ? [i === 0 ? 'English' : 'French'] : [])
      if (failed.length) throw new Error(`${failed.join(' and ')} could not be saved. Some changes may already be live. Retry the save.`)
      const [englishTable, frenchTable] = results.map(result => result.status === 'fulfilled' ? result.value : null)
      const english = englishTable?.strings ?? {}
      const french = frenchTable?.strings ?? {}
      setEntries(sortEntries(Array.from(new Set([...Object.keys(english), ...Object.keys(french)])).map(id => ({
        id, english: english[id] ?? '', french: french[id] ?? '', section: deriveSection(id)
      }))))
      setVersions({ en: englishTable?.version ?? 0, fr: frenchTable?.version ?? 0 })
      addToast({
        title: 'String table saved',
        description: 'Changes are live for the mobile app immediately.'
      })
      setInlineEdit(null)
      setFormError(null)
      return true
    } catch (error) {
      console.error('Failed to save string tables', error)
      addToast({
        title: 'Save failed',
        description: error instanceof Error ? error.message : 'Unable to save the string table. Please try again.'
      })
      return false
    } finally {
      setSaving(false)
    }
  }

  const startEdit = (entry: StringEntry) => {
    setInlineEdit({ entryId: entry.id, english: entry.english, french: entry.french })
    setFormError(null)
  }

  const startAdd = (section: string) => {
    const prefix = sectionToPrefix(section)
    setInlineEdit({ entryId: `__new_${section}`, english: '', french: '', newId: prefix, isNew: true })
    setFormError(null)
  }

  const cancelEdit = () => {
    setInlineEdit(null)
    setFormError(null)
  }

  const saveInlineEdit = async () => {
    if (!inlineEdit) return

    if (inlineEdit.isNew) {
      const normalizedId = normalizeId(inlineEdit.newId ?? '')
      if (!normalizedId) {
        setFormError('String ID is required.')
        return
      }
      if (!normalizedId.startsWith('ID_')) {
        setFormError('String IDs must start with ID_.')
        return
      }
      if (entries.some((e) => e.id === normalizedId)) {
        setFormError('That string ID already exists.')
        return
      }
      const newEntry: StringEntry = {
        id: normalizedId,
        english: inlineEdit.english.trim(),
        french: inlineEdit.french.trim(),
        section: deriveSection(normalizedId)
      }
      await persistEntries([...entries, newEntry])
    } else {
      const nextEntries = entries.map((entry) =>
        entry.id === inlineEdit.entryId
          ? { ...entry, english: inlineEdit.english.trim(), french: inlineEdit.french.trim() }
          : entry
      )
      await persistEntries(nextEntries)
    }
  }

  const deleteEntry = async (entry: StringEntry) => {
    if (inlineEdit) return

    const confirmed = window.confirm(
      `Delete ${entry.id} from the English and French string tables? This will go live immediately.`
    )
    if (!confirmed) return

    await persistEntries(entries.filter((candidate) => candidate.id !== entry.id))
  }

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleExportCsv = () => {
    const escapeCsvField = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
        return `"${value.replace(/"/g, '""')}"`
      }
      return value
    }

    const header = 'CATEGORY,ID,EN,FR'
    const rows = sortEntries(entries).map((entry) =>
      [entry.section, entry.id, entry.english, entry.french].map(escapeCsvField).join(',')
    )
    const csv = [header, ...rows].join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'hikestrong-string-table-export.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const parseCsv = (text: string): string[][] => {
    const rows: string[][] = []
    let i = 0
    while (i < text.length) {
      const row: string[] = []
      while (i < text.length) {
        if (text[i] === '"') {
          // Quoted field
          i++ // skip opening quote
          let field = ''
          while (i < text.length) {
            if (text[i] === '"') {
              if (i + 1 < text.length && text[i + 1] === '"') {
                field += '"'
                i += 2
              } else {
                i++ // skip closing quote
                break
              }
            } else {
              field += text[i]
              i++
            }
          }
          row.push(field)
        } else {
          // Unquoted field
          let field = ''
          while (i < text.length && text[i] !== ',' && text[i] !== '\n' && text[i] !== '\r') {
            field += text[i]
            i++
          }
          row.push(field)
        }
        if (i < text.length && text[i] === ',') {
          i++ // skip comma
        } else {
          break
        }
      }
      // Skip line endings
      if (i < text.length && text[i] === '\r') i++
      if (i < text.length && text[i] === '\n') i++
      // Skip empty rows
      if (row.length > 1 || (row.length === 1 && row[0].trim() !== '')) {
        rows.push(row)
      }
    }
    return rows
  }

  const handleImportCsv = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEdit) return

    const file = event.target.files?.[0]
    if (!file) return
    // Reset so re-selecting the same file triggers onChange again
    event.target.value = ''

    const reader = new FileReader()
    reader.onload = async (e) => {
      const text = e.target?.result as string
      if (!text) return

      const rows = parseCsv(text)
      if (rows.length < 2) {
        addToast({ title: 'Import failed', description: 'CSV file has no data rows.' })
        return
      }

      const headers = rows[0].map((h) => h.trim().toUpperCase())
      const idCol = headers.indexOf('ID')
      const enCol = headers.findIndex((h) => h === 'EN' || h === 'ENGLISH')
      const frCol = headers.findIndex((h) => h === 'FR' || h === 'FRENCH')

      if (idCol === -1) {
        addToast({ title: 'Import failed', description: 'CSV must have an ID column.' })
        return
      }

      const existingMap = new Map(entries.map((entry) => [entry.id, { ...entry }]))
      const existingIds = new Set(entries.map((entry) => entry.id))
      let newCount = 0
      let updatedCount = 0
      let skippedCount = 0

      for (let r = 1; r < rows.length; r++) {
        const row = rows[r]
        const rawId = row[idCol]?.trim()
        if (!rawId) continue
        const id = normalizeId(rawId)
        if (!id) continue

        const english = enCol !== -1 ? (row[enCol] ?? '') : ''
        const french = frCol !== -1 ? (row[frCol] ?? '') : ''

        if (skipExistingOnImport && existingIds.has(id)) {
          skippedCount++
          continue
        }

        const existing = existingMap.get(id)
        if (existing) {
          existing.english = english
          existing.french = french
          updatedCount++
        } else {
          existingMap.set(id, { id, english, french, section: deriveSection(id) })
          newCount++
        }
      }

      const merged = sortEntries(Array.from(existingMap.values()))
      if (!await persistEntries(merged)) return
      addToast({
        title: `Imported ${newCount + updatedCount} strings`,
        description: skipExistingOnImport
          ? `${newCount} new, ${updatedCount} updated, ${skippedCount} skipped existing.`
          : `${newCount} new, ${updatedCount} updated.`,
      })
    }
    reader.readAsText(file)
  }

  if (!isAdmin) {
    return (
      <Card className="border-dashed border-amber-500/40 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            Limited Access
          </CardTitle>
          <CardDescription>
            String tables can only be managed by Admin accounts. Contact an admin if you need access.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const isEditingEntry = (entryId: string) => inlineEdit?.entryId === entryId && !inlineEdit.isNew
  const isAddingInSection = (section: string) => inlineEdit?.entryId === `__new_${section}` && inlineEdit.isNew
  const isOwnerOverride = editAccess?.accessMode === 'ownerOverride'

  return (
    <div className="flex flex-1 flex-col min-h-0">
      {/* Fixed header: String Table manager + Browse Strings */}
      <div className="shrink-0 space-y-4 pb-4">
        {editAccess && (
          <Card className={canEdit ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'}>
            <CardContent className="flex items-start gap-3 py-4">
              {canEdit ? (
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              ) : (
                <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              )}
              <div>
                <div className="font-semibold text-foreground">
                  {isOwnerOverride
                    ? 'Owner editing override active'
                    : canEdit
                      ? 'Staging editing enabled'
                      : 'Read-only string tables'}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{editAccess.message}</p>
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-slate-100 p-3 ring-1 ring-slate-200">
                <ScrollText className="h-6 w-6 text-slate-700" />
              </div>
              <div>
                <CardTitle className="text-lg">String table manager</CardTitle>
                <CardDescription>
                  Marketing copy that powers the mobile app. {canEdit ? 'Updates are live in this environment immediately.' : 'Browse and export are available here; editing is disabled.'}
                </CardDescription>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-foreground font-semibold">
              <CheckCircle2 className="h-4 w-4" />
              EN v{versions.en || 0}
              <CheckCircle2 className="h-4 w-4" />
              FR v{versions.fr || 0}
              <Button
                variant="outline"
                size="sm"
                disabled={loading || entries.length === 0}
                onClick={handleExportCsv}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!canEdit || saving || loading}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                Import CSV
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleImportCsv}
              />
              <label className="flex items-center gap-2 text-xs normal-case tracking-normal text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-muted"
                  checked={skipExistingOnImport}
                  onChange={(event) => setSkipExistingOnImport(event.target.checked)}
                  disabled={!canEdit || saving || loading}
                />
                <span>Skip existing IDs</span>
              </label>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Use IDs like <span className="font-semibold text-foreground">ID_HOME_TITLE</span>. Keep English and French aligned for localization.
            </p>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                <Info className="h-3.5 w-3.5" />
                Variable placeholders
              </div>
              <p className="text-xs text-slate-600">
                Use <code className="rounded bg-violet-100 px-1 py-0.5 text-violet-700 ring-1 ring-violet-200">{'{name}'}</code> syntax to insert dynamic values at runtime.
                Both English and French must use the same placeholder names.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                <span><code className="font-semibold text-slate-700">{'{count}'}</code> — a number</span>
                <span><code className="font-semibold text-slate-700">{'{name}'}</code> — user&apos;s name</span>
                <span><code className="font-semibold text-slate-700">{'{distance}'}</code> — distance value</span>
                <span><code className="font-semibold text-slate-700">{'{duration}'}</code> — time duration</span>
                <span><code className="font-semibold text-slate-700">{'{goal}'}</code> — goal label</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Browse strings</CardTitle>
                <CardDescription>
                  Search by ID or copy. Filter by section to focus on a specific screen.
                </CardDescription>
              </div>
              <Button variant="ghost" size="icon" onClick={loadTables} title="Reload string tables">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="md:col-span-2">
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search by ID or copy…"
                />
              </div>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={sectionFilter}
                onChange={(event) => setSectionFilter(event.target.value)}
              >
                {sections.map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{filteredEntries.length} strings visible</span>
              <div className="flex items-center gap-1">
                <Search className="h-3.5 w-3.5" />
                Filtered view
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scrollable string entries */}
      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {loading ? (
          <Card className="shadow-sm">
            <CardContent className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading string tables…
            </CardContent>
          </Card>
        ) : groupedEntries.length === 0 ? (
          searchTerm ? (
            <SearchEmptyState searchTerm={searchTerm} />
          ) : (
            <EmptyState
              icon={ScrollText}
              title="No strings yet"
              description="Add your first string ID and copy to start populating the app's localization table."
            />
          )
        ) : (
          groupedEntries.map((group) => (
            <Card key={group.section} className="shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{group.section}</CardTitle>
                    <CardDescription>{group.entries.length} string{group.entries.length === 1 ? '' : 's'}</CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startAdd(group.section)}
                    disabled={!canEdit || saving || !!inlineEdit}
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {group.entries.map((entry) =>
                  isEditingEntry(entry.id) ? (
                    <InlineEditRow
                      key={entry.id}
                      entryId={entry.id}
                      edit={inlineEdit!}
                      setEdit={setInlineEdit}
                      formError={formError}
                      saving={saving}
                      onSave={saveInlineEdit}
                      onCancel={cancelEdit}
                    />
                  ) : (
                    <div key={entry.id} className="rounded-lg border border-border p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-foreground">{entry.id}</p>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(entry)}
                            disabled={!canEdit || saving || !!inlineEdit}
                          >
                            <Pencil className="mr-1.5 h-3.5 w-3.5" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void deleteEntry(entry)}
                            disabled={!canEdit || saving || !!inlineEdit}
                            className="text-red-600 hover:text-red-700"
                            title={`Delete ${entry.id}`}
                          >
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                            Delete
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 grid gap-3 md:grid-cols-2">
                        <div className="rounded-md bg-muted/40 p-3">
                          <p className="text-xs font-semibold text-muted-foreground">English</p>
                          <p className="text-sm text-foreground break-words">
                            <HighlightedValue text={entry.english} />
                          </p>
                        </div>
                        <div className="rounded-md bg-muted/40 p-3">
                          <p className="text-xs font-semibold text-muted-foreground">French</p>
                          <p className="text-sm text-foreground break-words">
                            <HighlightedValue text={entry.french} />
                          </p>
                        </div>
                      </div>
                      <PlaceholderMismatchWarning english={entry.english} french={entry.french} />
                    </div>
                  )
                )}

                {/* Inline add row at the bottom of the section */}
                {isAddingInSection(group.section) && (
                  <InlineEditRow
                    entryId={inlineEdit!.entryId}
                    edit={inlineEdit!}
                    setEdit={setInlineEdit}
                    formError={formError}
                    saving={saving}
                    onSave={saveInlineEdit}
                    onCancel={cancelEdit}
                    isNew
                  />
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}

function InlineEditRow({
  entryId,
  edit,
  setEdit,
  formError,
  saving,
  onSave,
  onCancel,
  isNew,
}: {
  entryId: string
  edit: InlineEdit
  setEdit: React.Dispatch<React.SetStateAction<InlineEdit | null>>
  formError: string | null
  saving: boolean
  onSave: () => void
  onCancel: () => void
  isNew?: boolean
}) {
  const idPrefix = `inline-edit-${(isNew ? 'new' : entryId).replace(/[^a-zA-Z0-9_-]/g, '-')}`

  return (
    <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-3 space-y-3">
      {isNew ? (
        <div className="space-y-1">
          <label className="text-xs font-semibold text-muted-foreground" htmlFor={`${idPrefix}-string-id`}>
            String ID
          </label>
          <Input
            id={`${idPrefix}-string-id`}
            value={edit.newId ?? ''}
            onChange={(e) => setEdit((prev) => prev ? { ...prev, newId: e.target.value } : prev)}
            placeholder="ID_SECTION_NAME"
            disabled={saving}
          />
        </div>
      ) : (
        <p className="text-sm font-semibold text-foreground">{entryId}</p>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-muted-foreground" htmlFor={`${idPrefix}-english`}>
            English
          </label>
          <Textarea
            id={`${idPrefix}-english`}
            rows={3}
            value={edit.english}
            onChange={(e) => setEdit((prev) => prev ? { ...prev, english: e.target.value } : prev)}
            placeholder="English copy"
            // Locked while saving, so the saved copy is what's on screen (BH-14).
            disabled={saving}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-muted-foreground" htmlFor={`${idPrefix}-french`}>
            French
          </label>
          <Textarea
            id={`${idPrefix}-french`}
            rows={3}
            value={edit.french}
            onChange={(e) => setEdit((prev) => prev ? { ...prev, french: e.target.value } : prev)}
            placeholder="French copy"
            disabled={saving}
          />
        </div>
      </div>
      <PlaceholderMismatchWarning english={edit.english} french={edit.french} />
      {formError && <p className="text-sm text-destructive">{formError}</p>}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={onSave} disabled={saving}>
          {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />}
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
          <X className="mr-1.5 h-3.5 w-3.5" />
          Cancel
        </Button>
      </div>
    </div>
  )
}
